import applePay from 'braintree-web/apple-pay';
import braintreeClient from 'braintree-web/client';
import dataCollector from 'braintree-web/data-collector';
import googlePayment from 'braintree-web/google-payment';
import hostedFields from 'braintree-web/hosted-fields';
import paypalCheckout from 'braintree-web/paypal-checkout';

// the Google Pay JS library, which Google requires us to load from their domain
const GOOGLE_PAY_SCRIPT = 'https://pay.google.com/gp/p/js/pay.js';

// the Apple Pay JS API version we target; 3 is the lowest version that supports
// everything we need and is available on every Safari that supports Apple Pay
const APPLE_PAY_VERSION = 3;

// the Google Pay API version Braintree expects us to use
const GOOGLE_PAY_VERSION = 2;

/*
 * How long we wait for PayPal to draw its button before giving up on it.
 * Measured at a little under a second against the live gateway, so this is
 * generous without leaving anyone staring at an empty panel; the form shows a
 * spinner meanwhile, so the wait is visible rather than silent.
 */
const PAYPAL_RENDER_TIMEOUT_MS = 5000;

/* Loads a script once, returning a promise that resolves when it is ready. */
const loadScript = (src) => {
  // already loaded (or loading) it?
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) {
    // if it already finished, we're done
    if (existing.dataset.loaded === 'true') {
      return Promise.resolve();
    }

    // otherwise, wait on the in-flight load
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', reject);
    });
  }

  // load it
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
};

/**
 * Wraps the Braintree SDK components that, together, replace the deprecated
 * Braintree drop-in: Hosted Fields for cards, plus the PayPal, Apple Pay and
 * Google Pay components for the wallets. Instances of this class own the
 * lifecycle of every SDK object they create, so tearing the controller down
 * tears all of them down.
 *
 * Nothing in here touches React; the component drives it.
 */
export default class BraintreeController {
  constructor(authorization) {
    // the client token we authorize every component with
    this.authorization = authorization;

    // the SDK instances we create, all of which need tearing down
    this.client = null;
    this.dataCollectorInstance = null;
    this.hostedFieldsInstance = null;
    this.payPalInstance = null;
    this.applePayInstance = null;
    this.googlePayInstance = null;

    // the Google Pay client, which belongs to Google rather than Braintree
    this.googlePayClient = null;

    // set once teardown starts, so late callbacks can bail out
    this.destroyed = false;
  }

  /* Creates the Braintree client that every other component is built on. */
  async createClient() {
    this.client = await braintreeClient.create({ authorization: this.authorization });
    return this.client;
  }

  /* Returns 'sandbox' or 'production', per the client token. */
  environment() {
    return this.client?.getConfiguration()?.gatewayConfiguration?.environment || 'sandbox';
  }

  /**
   * Starts device-data collection for fraud screening. This replaces the
   * drop-in's `dataCollector: true` option; see PNG-552.
   */
  async createDataCollector() {
    this.dataCollectorInstance = await dataCollector.create({ client: this.client });
    return this.dataCollectorInstance;
  }

  /* Returns the collected device data, or null if collection was unavailable. */
  async getDeviceData() {
    if (!this.dataCollectorInstance) {
      return null;
    }

    try {
      return await this.dataCollectorInstance.getDeviceData();
    } catch (e) {
      // device data is best-effort; a payment should not fail because we
      // could not collect it
      console.error('Error collecting Braintree device data', e);
      return null;
    }
  }

  /**
   * Mounts Hosted Fields into the given containers. Card data is entered into
   * Braintree-hosted iframes, so it never touches our DOM or our servers.
   */
  async createHostedFields({ selectors, styles, onValidityChange, onCardTypeChange }) {
    this.hostedFieldsInstance = await hostedFields.create({
      client: this.client,
      styles: styles,
      fields: {
        cardholderName: {
          container: selectors.cardholderName,
          placeholder: 'Name on card',
        },
        number: {
          container: selectors.number,
          placeholder: '•••• •••• •••• ••••',
        },
        expirationDate: {
          container: selectors.expirationDate,
          placeholder: 'MM / YY',
        },
        cvv: {
          container: selectors.cvv,
          placeholder: '•••',
        },
      },
    });

    // report validity so the component can gate the submit button
    if (onValidityChange) {
      this.hostedFieldsInstance.on('validityChange', (event) => {
        const allValid = Object.keys(event.fields).every((key) => event.fields[key].isValid);
        onValidityChange(allValid, event.fields);
      });

      // 'validityChange' does not fire on mount, so report the initial state
      const initial = this.hostedFieldsInstance.getState();
      const initiallyValid = Object.keys(initial.fields).every(
        (key) => initial.fields[key].isValid,
      );
      onValidityChange(initiallyValid, initial.fields);
    }

    // report the card type so we can show the matching brand
    if (onCardTypeChange) {
      this.hostedFieldsInstance.on('cardTypeChange', (event) => {
        onCardTypeChange(event.cards?.length === 1 ? event.cards[0] : null);
      });
    }

    return this.hostedFieldsInstance;
  }

  /**
   * Tokenizes the card currently in Hosted Fields, tokenizing the supplied
   * billing address along with it so that the gateway can run AVS. The
   * drop-in gave us no way to do this.
   */
  async tokenizeCard(billingAddress) {
    if (!this.hostedFieldsInstance) {
      throw new Error('Hosted Fields are not available');
    }

    const options = {};
    if (billingAddress) {
      options.billingAddress = billingAddress;
    }

    const payload = await this.hostedFieldsInstance.tokenize(options);
    return payload.nonce;
  }

  /**
   * Creates the PayPal component and loads PayPal's own SDK. The button is
   * rendered separately, since PayPal needs a visible container to render into.
   */
  async setupPayPal() {
    this.payPalInstance = await paypalCheckout.create({ client: this.client });

    // PayPal's button comes from PayPal's SDK, which Braintree loads for us
    await this.payPalInstance.loadPayPalSDK({ currency: 'USD', intent: 'authorize' });

    // bail out if we were torn down while the SDK was loading
    if (this.destroyed) {
      return false;
    }

    if (!window.paypal?.Buttons) {
      throw new Error('PayPal SDK is unavailable');
    }

    return true;
  }

  /**
   * Renders PayPal's button into the given container. `getAmount` is called
   * when the button is clicked, so we always authorize the current amount.
   */
  async renderPayPalButton({ container, getAmount, onNonce, onError }) {
    // these are all programming or load-order errors, so say which one it was
    // rather than leaving an empty space where the button should be
    const node = document.querySelector(container);
    if (!node) {
      throw new Error(`PayPal container '${container}' is not in the document`);
    }
    if (!this.payPalInstance) {
      throw new Error('The PayPal component has not been created');
    }
    if (!window.paypal?.Buttons) {
      throw new Error("PayPal's SDK did not load");
    }

    /*
     * PayPal measures the container as it renders and bakes the result into the
     * button's own height. Render into a hidden container and it reports success
     * while producing a button fixed at height 0, which stays invisible even
     * after the container is shown: no button, no error, nothing to debug. So
     * refuse to render into one rather than fail silently.
     */
    if (node.getClientRects().length === 0 || node.offsetWidth === 0) {
      throw new Error('The PayPal container is hidden; its button would render with no height');
    }

    // clear anything a previous render left behind
    node.innerHTML = '';

    const buttons = window.paypal.Buttons({
      style: { label: 'paypal', height: 45 },
      fundingSource: window.paypal.FUNDING.PAYPAL,

      createOrder: () =>
        this.payPalInstance.createPayment({
          flow: 'checkout',
          intent: 'authorize',
          currency: 'USD',
          amount: getAmount(),
        }),

      onApprove: (data) =>
        this.payPalInstance
          .tokenizePayment(data)
          .then((payload) => onNonce(payload.nonce, 'payPal'))
          .catch(onError),

      onError: onError,

      // the customer closing the PayPal window is not an error
      onCancel: () => {},
    });

    // PayPal decides for itself whether it can offer this funding source; if it
    // cannot, rendering rejects, so check first and say so plainly
    if (typeof buttons.isEligible === 'function' && !buttons.isEligible()) {
      throw new Error('PayPal reports that its button is not eligible to render here');
    }

    // PayPal's render resolves only once its own iframe reports back, and an
    // extension or a network fault can leave that promise pending forever,
    // which would strand the customer on an empty panel. Give it a deadline.
    await Promise.race([
      buttons.render(container),
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error('PayPal took too long to render its button')),
          PAYPAL_RENDER_TIMEOUT_MS,
        ),
      ),
    ]);

    // and make sure what it drew is actually something the customer can click
    if (node.offsetHeight === 0) {
      throw new Error('PayPal rendered a button with no height');
    }
  }

  /* True if this browser and device can pay with Apple Pay. */
  static applePaySupported() {
    try {
      return Boolean(window.ApplePaySession && window.ApplePaySession.canMakePayments());
    } catch {
      // Safari throws rather than returning false on insecure origins
      return false;
    }
  }

  /* Creates the Apple Pay component. */
  async setupApplePay() {
    this.applePayInstance = await applePay.create({ client: this.client });
    return this.applePayInstance;
  }

  /**
   * Runs an Apple Pay payment. This must be called directly from a click
   * handler: Safari only allows an ApplePaySession to be created inside a
   * user gesture, so nothing may be awaited before the session is constructed.
   */
  startApplePay({ amount, displayName, onNonce, onError }) {
    if (!this.applePayInstance) {
      onError(new Error('Apple Pay is not available'));
      return;
    }

    // build the request and open the sheet synchronously
    const paymentRequest = this.applePayInstance.createPaymentRequest({
      total: { label: displayName, amount: String(amount) },
      requiredBillingContactFields: ['postalAddress'],
    });
    const session = new window.ApplePaySession(APPLE_PAY_VERSION, paymentRequest);

    // Apple requires us to prove we are a registered merchant for this domain
    session.onvalidatemerchant = (event) => {
      this.applePayInstance
        .performValidation({
          validationURL: event.validationURL,
          displayName: displayName,
        })
        .then((merchantSession) => session.completeMerchantValidation(merchantSession))
        .catch((err) => {
          session.abort();
          onError(err);
        });
    };

    // exchange Apple's token for a Braintree nonce
    session.onpaymentauthorized = (event) => {
      this.applePayInstance
        .tokenize({ token: event.payment.token })
        .then((payload) => {
          session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
          onNonce(payload.nonce, 'applePay', event.payment.billingContact);
        })
        .catch((err) => {
          session.completePayment(window.ApplePaySession.STATUS_FAILURE);
          onError(err);
        });
    };

    // the customer dismissing the sheet is not an error
    session.oncancel = () => {};

    session.begin();
  }

  /**
   * Creates the Google Pay component and Google's own payments client, and
   * reports whether this browser can actually pay with Google Pay.
   */
  async setupGooglePay(merchantId) {
    this.googlePayInstance = await googlePayment.create({
      client: this.client,
      googlePayVersion: GOOGLE_PAY_VERSION,
      googleMerchantId: merchantId,
    });

    // Google's button and payment sheet come from Google's own library
    await loadScript(GOOGLE_PAY_SCRIPT);

    // bail out if we were torn down while the script was loading
    if (this.destroyed) {
      return false;
    }

    if (!window.google?.payments?.api?.PaymentsClient) {
      throw new Error('Google Pay SDK is unavailable');
    }

    this.googlePayClient = new window.google.payments.api.PaymentsClient({
      environment: this.environment() === 'production' ? 'PRODUCTION' : 'TEST',
    });

    // ask Google whether this customer can pay
    const request = this.googlePayInstance.createPaymentDataRequest();
    const response = await this.googlePayClient.isReadyToPay({
      apiVersion: request.apiVersion,
      apiVersionMinor: request.apiVersionMinor,
      allowedPaymentMethods: request.allowedPaymentMethods,
      existingPaymentMethodRequired: false,
    });

    return Boolean(response?.result);
  }

  /**
   * Renders Google's own branded button into the given container. Google
   * requires their button rather than one of ours.
   */
  renderGooglePayButton({ container, onClick }) {
    const node = document.querySelector(container);
    if (!node || !this.googlePayClient) {
      return;
    }

    // clear anything a previous render left behind
    node.innerHTML = '';
    node.appendChild(
      this.googlePayClient.createButton({
        onClick: onClick,
        buttonType: 'plain',
        buttonSizeMode: 'fill',
      }),
    );
  }

  /* Runs a Google Pay payment. */
  async startGooglePay({ amount, onNonce, onError }) {
    if (!this.googlePayInstance || !this.googlePayClient) {
      onError(new Error('Google Pay is not available'));
      return;
    }

    try {
      // ask for the payment data, which opens Google's sheet
      const paymentData = await this.googlePayClient.loadPaymentData(
        this.googlePayInstance.createPaymentDataRequest({
          transactionInfo: {
            currencyCode: 'USD',
            totalPriceStatus: 'FINAL',
            totalPrice: String(amount),
          },
          emailRequired: false,
          billingAddressRequired: true,
          billingAddressParameters: { format: 'FULL' },
        }),
      );

      // exchange Google's response for a Braintree nonce
      const payload = await this.googlePayInstance.parseResponse(paymentData);
      onNonce(payload.nonce, 'googlePay', paymentData);
    } catch (err) {
      // the customer closing the sheet is not an error
      if (err?.statusCode === 'CANCELED') {
        return;
      }
      onError(err);
    }
  }

  /* Tears down every SDK instance we created. */
  async teardown() {
    this.destroyed = true;

    // each teardown is independent, and a failure in one should not prevent
    // the others from running
    const instances = [
      this.hostedFieldsInstance,
      this.payPalInstance,
      this.applePayInstance,
      this.googlePayInstance,
      this.dataCollectorInstance,
    ];

    await Promise.all(
      instances.map(async (instance) => {
        if (!instance?.teardown) {
          return;
        }
        try {
          await instance.teardown();
        } catch (e) {
          console.error('Error tearing down Braintree component', e);
        }
      }),
    );

    this.hostedFieldsInstance = null;
    this.payPalInstance = null;
    this.applePayInstance = null;
    this.googlePayInstance = null;
    this.dataCollectorInstance = null;
    this.googlePayClient = null;
    this.client = null;
  }
}
